// Pure timerlogica op basis van tijdstempels (geen setInterval-telling), zodat de timer
// klopt na vergrendelen, achtergrond of herladen. `now` is altijd een parameter (testbaar).
// Port van legacy/app.js sectie 8 (startTimer, getTimerRemainingMs, formatTimerClock, loadActiveTimerState).
import type { ActiveTimer, ActivityType, TimerActivity } from './types';

export const DEFAULT_TIMER_MINUTES = 30;

function plannedMinutes(activity: TimerActivity): number {
  const n = Number(activity.duurMinuten);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_TIMER_MINUTES;
}

export function startTimer(activity: TimerActivity, now: number): ActiveTimer {
  const duurMinuten = plannedMinutes(activity);
  return {
    activity: { ...activity, duurMinuten },
    startedAt: now,
    endsAt: now + duurMinuten * 60_000,
    pausedRemainingMs: null,
  };
}

export function remainingMs(timer: ActiveTimer, now: number): number {
  if (timer.pausedRemainingMs !== null && timer.pausedRemainingMs !== undefined) {
    return Math.max(0, timer.pausedRemainingMs);
  }
  return Math.max(0, timer.endsAt - now);
}

/** Alleen een lopende (niet-gepauzeerde) timer kan verlopen. */
export function isExpired(timer: ActiveTimer, now: number): boolean {
  return timer.pausedRemainingMs === null && remainingMs(timer, now) <= 0;
}

export function pauseTimer(timer: ActiveTimer, now: number): ActiveTimer {
  if (timer.pausedRemainingMs !== null) return timer;
  return { ...timer, pausedRemainingMs: remainingMs(timer, now) };
}

export function resumeTimer(timer: ActiveTimer, now: number): ActiveTimer {
  if (timer.pausedRemainingMs === null) return timer;
  return { ...timer, endsAt: now + Math.max(0, timer.pausedRemainingMs), pausedRemainingMs: null };
}

/** Werkelijk gespeelde minuten (minimaal 1), rekening houdend met pauze. */
export function elapsedMinutes(timer: ActiveTimer, now: number): number {
  const plannedMs = plannedMinutes(timer.activity) * 60_000;
  return Math.max(1, Math.round((plannedMs - remainingMs(timer, now)) / 60_000));
}

/** "12:34" (of "1:02:03" boven het uur). Rondt naar boven af op hele seconden. */
export function formatClock(ms: number): string {
  const safe = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  const totalSec = Math.ceil(safe / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const ss = String(totalSec % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

function parseActivityId(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (value === '') return null;
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * Veilig parsen van bewaarde timer; null bij corrupte data.
 * Accepteert zowel de JSON-string uit localStorage als een al geparst object
 * (legacy-vorm `{activity, startedAt, endsAt, pausedRemainingMs}`).
 */
export function parseStoredTimer(raw: unknown): ActiveTimer | null {
  let s: unknown = raw;
  if (typeof raw === 'string') {
    try {
      s = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
  const o = s as Record<string, unknown>;
  const act = o.activity;
  if (!act || typeof act !== 'object' || Array.isArray(act)) return null;
  if (typeof o.startedAt !== 'number' || !Number.isFinite(o.startedAt)) return null;
  if (typeof o.endsAt !== 'number' || !Number.isFinite(o.endsAt)) return null;
  const a = act as Record<string, unknown>;
  const paused =
    typeof o.pausedRemainingMs === 'number' && Number.isFinite(o.pausedRemainingMs)
      ? Math.max(0, o.pausedRemainingMs)
      : null;
  const type: ActivityType = String(a.type ?? '').toLowerCase().includes('buiten') ? 'Buiten' : 'Binnen';
  const duur = Number(a.duurMinuten);
  return {
    activity: {
      id: parseActivityId(a.id),
      naam: String(a.naam || 'Missie'),
      duurMinuten: Number.isFinite(duur) && duur > 0 ? duur : DEFAULT_TIMER_MINUTES,
      type,
      energie: typeof a.energie === 'string' && a.energie ? a.energie : 'Normaal',
    },
    startedAt: o.startedAt,
    endsAt: o.endsAt,
    pausedRemainingMs: paused,
  };
}
